import React, { useEffect, useState, useMemo } from 'react';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile, Molecule } from '../../types/chemistry';
import apiClient from '../../api/client';

interface ProjectADMETTabProps {
  projectId: number;
}

interface ADMETResult {
  moleculeId: number;
  smiles: string;
  name: string;
  fileId: string;
  fileName: string;
  predictions: Record<string, any> | null;
  error?: string;
}

const CHEM_EXT = new Set(['sdf','mol','mol2','smi','smiles','csv','xlsx','xls','cdx','cdxml','cml','inchi','rxn','rdf']);

const isChemFile = (f: ChemFile) => {
  if (f.fileType) return f.fileType === 'molecules';
  // No fileType set — fall back to extension check (backward compat for older files)
  // Check originalFileName first, then fileName (originalFileName may lack extension after rename)
  const origExt = (f.originalFileName || '').split('.').pop()?.toLowerCase();
  if (origExt && CHEM_EXT.has(origExt)) return true;
  const ext = (f.fileName || '').split('.').pop()?.toLowerCase();
  return !!ext && CHEM_EXT.has(ext);
};

const cleanFileName = (name: string) => name.replace(/^[a-f0-9]{8}_/i, '');

const ProjectADMETTab: React.FC<ProjectADMETTabProps> = ({ projectId }) => {
  const [files, setFiles] = useState<ChemFile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<ADMETResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [hasRun, setHasRun] = useState(false);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const allFiles = await chemistryApi.getFiles(projectId);
        setFiles(allFiles.filter(isChemFile));
      } catch { /* ignore */ }
      finally { setIsLoading(false); }
    })();
  }, [projectId]);

  const totalMolecules = files.reduce((sum, f) => sum + f.moleculesLibraryCount, 0);

  const runADMET = async () => {
    setIsRunning(true);
    setResults([]);
    setHasRun(true);
    const allResults: ADMETResult[] = [];
    let done = 0;
    const total = Math.min(totalMolecules, 200); // Cap at 200 for performance
    setProgress({ done: 0, total });

    for (const file of files) {
      try {
        const { molecules } = await chemistryApi.getMolecules(file.id, 1, 50);
        const fileName = cleanFileName(file.originalFileName || file.fileName);
        for (const mol of molecules) {
          if (done >= 200) break;
          if (!mol.smiles) continue;
          try {
            const resp = await apiClient.post('/v2/admet/predict', { smiles: mol.smiles });
            allResults.push({
              moleculeId: mol.id,
              smiles: mol.smiles,
              name: mol.properties?.Name || mol.properties?.name || `Mol ${mol.id}`,
              fileId: file.id,
              fileName,
              predictions: resp.data,
            });
          } catch {
            allResults.push({
              moleculeId: mol.id,
              smiles: mol.smiles,
              name: mol.properties?.Name || mol.properties?.name || `Mol ${mol.id}`,
              fileId: file.id,
              fileName,
              predictions: null,
              error: 'Prediction failed',
            });
          }
          done++;
          setProgress({ done, total });
          setResults([...allResults]);
        }
      } catch { /* skip file */ }
      if (done >= 200) break;
    }
    setIsRunning(false);
  };

  const colorFor = (ok: boolean) => ok ? 'text-green-600' : 'text-red-600';
  const bgFor = (ok: boolean) => ok ? 'bg-green-50' : 'bg-red-50';

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">Loading project data...</span>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-24 h-24 mb-6 bg-gradient-to-br from-amber-100 to-orange-50 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        </div>
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">No compounds for ADMET analysis</p>
        <p className="text-gray-400 mt-2">Upload compound files to run ADMET predictions.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto w-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-800 dark:text-gray-200">ADMET Predictions</h2>
          <p className="text-sm text-gray-500 mt-1">
            Absorption, Distribution, Metabolism, Excretion & Toxicity
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isRunning && (
            <span className="text-sm text-gray-500">
              {progress.done}/{progress.total} compounds
            </span>
          )}
          <button
            onClick={runADMET}
            disabled={isRunning}
            className="px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-semibold rounded-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
          >
            {isRunning ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Running...
              </>
            ) : hasRun ? 'Re-run ADMET' : 'Run ADMET Analysis'}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      {isRunning && (
        <div className="mb-6">
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-amber-500 to-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress.total > 0 ? (progress.done / progress.total) * 100 : 0}%` }}
            />
          </div>
        </div>
      )}

      {/* Results table */}
      {results.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-800">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Compound</th>
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">File</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Lipinski</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">MW</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">LogP</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">HBD</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">HBA</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">TPSA</th>
                  <th className="text-center px-4 py-3 text-xs font-bold text-gray-400 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r) => {
                  const p = r.predictions;
                  const lipinski = p ? (p.lipinski_violations === 0 || p.lipinski_pass) : null;
                  return (
                    <tr key={r.moleculeId} className="border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[200px] truncate">{r.name}</td>
                      <td className="px-4 py-2.5 text-gray-500 max-w-[150px] truncate">{r.fileName}</td>
                      <td className="px-4 py-2.5 text-center">
                        {lipinski != null ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${bgFor(lipinski)} ${colorFor(lipinski)}`}>
                            {lipinski ? 'Pass' : 'Fail'}
                          </span>
                        ) : r.error ? (
                          <span className="text-gray-300">-</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">{p?.mw?.toFixed(1) ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">{p?.logp?.toFixed(2) ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">{p?.hbd ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">{p?.hba ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center text-gray-600 dark:text-gray-400">{p?.tpsa?.toFixed(1) ?? '-'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {r.error ? (
                          <span className="text-xs text-red-400">{r.error}</span>
                        ) : (
                          <span className="text-xs text-green-500">OK</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty state before running */}
      {!hasRun && results.length === 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-12 text-center">
          <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-100 to-orange-50 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">
            {totalMolecules.toLocaleString()} compounds across {files.length} files
          </p>
          <p className="text-gray-400 mt-2 max-w-md mx-auto">
            Click "Run ADMET Analysis" to predict drug-likeness, Lipinski rules, and ADMET properties for your compounds.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectADMETTab;
